import {
    ActionFunctionArgs,
    Form,
    LoaderFunctionArgs,
    redirect,
    useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import {Input, TextArea} from "../../components/inputs/Input";
import PriceInput from "../../components/inputs/PriceInput";
import {FormLayout} from "../../layout/FormLayout";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {Item, Categories, ImageLibrary} from "../../types/item";
import {GET_ITEM, GET_ITEM_CATEGORIES} from "./queries";
import {ensureToken} from "../tokens/registry";
import {CREATE_ITEM} from "./mutations";
import {TagsInput} from "../../components/inputs/TagsInput";
import {useEffect, useState} from "react";
import {userStore} from "../../store/user";
import CustomSelect from "../../components/customSelect";
import {
    GET_LIST_EVENTS_BY_ORGANIZATION,
    GET_LIST_VENDORS_BY_ORGANIZATION,
} from "../../queries";
import {organizationStore} from "../../store/organization";
import {Vendor} from "../../types/vendor";
import {Event} from "../../types/event";
import {AlternateTax} from "./type";
import {inclusiveBaseCents, menuPriceCentsOf} from "../helpers/pricing";
import CompactThumbUploader from "../../components/CompactThumbUploader";
import {GET_IMAGE_LIBRARY_ITEMS_LIMITED} from "../image-library/queries";
import ModifierSelector from "./components/ModifierSelector";

const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationStore} = organizationStore;

export const itemCloneLoader = async ({params}: LoaderFunctionArgs) => {
    const organizationId = getOrganizationStore().organizationId;
    const user = getState().user;

    let eventWhere = {};
    let vendorWhere = {};

    if (user?.vendors) {
        vendorWhere = {
            ...vendorWhere,
            id: {
                _in: user?.vendors,
            },
        };
    }

    if (user?.events) {
        eventWhere = {
            ...eventWhere,
            id: {
                _in: user?.events,
            },
        };
    }

    if (organizationId) {
        vendorWhere = {
            ...vendorWhere,
            organization_id: {
                _eq: organizationId,
            },
        };

        eventWhere = {
            ...eventWhere,
            organization_id: {_eq: organizationId},
            is_active: {_eq: true},
        };
    }
    const {id} = params;
    const [
        {data: itemData},
        {data: vendorsData},
        {data: eventData},
        {data: itemCategoriesData},
    ] = await Promise.all([
        client.query({
            query: GET_ITEM,
            variables: {id},
        }),
        client.query({
            query: GET_LIST_VENDORS_BY_ORGANIZATION,
            variables: {
                where: vendorWhere,
            },
        }),
        client.query({
            query: GET_LIST_EVENTS_BY_ORGANIZATION,
            variables: {
                where: eventWhere,
            },
        }),
        client.query({
            query: GET_ITEM_CATEGORIES,
            variables: {
                where: organizationId
                    ? {
                        organization_id: {
                            _in: [0, organizationId],
                        },
                        is_active: {
                            _eq: true,
                        },
                        reference_type: {
                            _eq: "item_category",
                        },
                    }
                    : {},
            },
        }),
    ]);

    const {data: imageData} = await client.query({
        query: GET_IMAGE_LIBRARY_ITEMS_LIMITED,
        variables: {
            where: eventWhere,
        },
    });

    return {
        item: itemData.items_by_pk,
        vendors: vendorsData.vendors,
        events: eventData.events,
        itemCategories: itemCategoriesData.dashboard_reference,
        images: imageData.image_library
    };
};

export const itemCloneAction = async ({
                                          params,
                                          request,
                                      }: ActionFunctionArgs) => {
    const user = getState().user;
    const body = await request.formData();
    const applyInclusivePrice =
        body.get("taxable_item") === "exclusive" ? true : false;
    const inclusivePriceString = body.get("inclusive_price") as string;
    const inclusivePrice = parseFloat(
        inclusivePriceString.length ? inclusivePriceString : "0"
    );
    const priceString = body.get("price") as string;
    const price = parseFloat(priceString);
    const tagsString = body.get("tags") as string;
    const tags = tagsString.split(",").map((tag) => tag.trim());
    const taxPercentageString = body.get("tax_percentage") as string;
    const taxPercentage = (parseFloat(taxPercentageString) / 100).toFixed(8);
    const redeemable_token_id = body.get("redeemable_token_name");
    const modifiers = JSON.parse(body.get("selected_item_modifiers") as string);
    const modifierArray = [...modifiers];
    const alternateTax = JSON.parse(body.get("alternate_tax") as string);
    const alternateTaxArray = [...alternateTax];

    let input = {
        name: body.get("name"),
        is_active: body.get("is_active"),
        event_id: body.get("event_id"),
        vendor_id: body.get("vendor_id"),
        category: body.get("category"),
        description: body.get("description"),
        upc: body.get("upc"),
        price: Math.round((applyInclusivePrice ? price : inclusivePrice) * 100),
        // RP-1955: the typed menu price in cents; the tablets charge an inclusive item at this amount
        menu_price: price > 0 ? Math.round(price * 100) : null,
        tags,
        gl_account_number: body.get("gl_account_number"),
        gl_account_name: body.get("gl_account_name"),
        is_variable_price: body.get("is_variable_price"),
        redeemable_token_id: redeemable_token_id,
        redeemable_token_name: redeemable_token_id,
        token_price: body.get("token_price"),
        short_name: body.get("name"),
        tax: parseFloat(taxPercentage) !== 0 ? "taxed" : "no_tax",
        taxable_item: body.get("taxable_item") === "exclusive" ? true : false,
        tax_percentage: taxPercentage ? taxPercentage : 0,
        last_updated_by: user?.id,
        alternate_tax: alternateTaxArray,
        modifiers: modifierArray,
        modifier_type: body.get("modifier_type"),
        tax_type: body.get("taxable_item"),
        image: body.get("image") || null,
    };

    try {
        const {data} = await client.mutate({
            mutation: CREATE_ITEM,
            variables: {
                input,
            },
        });

        // Token rename fix: register the cloned item's token name.
        if (redeemable_token_id) {
            await ensureToken(
                client,
                Number(body.get("event_id")),
                String(redeemable_token_id)
            );
        }

        toast.success("Item Cloned");
        return redirect(`/items/list`);
    } catch (error) {
        return toast.error("Failed To Clone Item" + error);
    }
};

export const ItemClone = () => {
    const {item, vendors, events, itemCategories, images} = useLoaderData() as {
        item: Item;
        vendors: Vendor[];
        events: Event[];
        itemCategories: Categories[];
        images: ImageLibrary[];
    };
    const imageOptions = images.map((item: { image_base64: string; display_name: string; }) => ({
        value: item.image_base64,
        label: item.display_name,
        display_name: item.display_name,
        image_base64: item.image_base64,
    }));
    const [taxed, setTaxed] = useState(
        item.taxable_item ? "exclusive" : "inclusive"
    );
    const [taxPercentage, setTaxPercentage] = useState<number>(
        Number(((item.tax_percentage * 100) / 1).toFixed(4))
    );
    const [isAddClicked, setIsAddClicked] = useState(false);
    const [totalTax, setTotalTax] = useState(0);
    // RP-1955: the stored menu price when the item has one, else the legacy derivation from the base
    const [price, setPrice] = useState<number>(menuPriceCentsOf(item) / 100);
    const [inclusivePrice, setInclusivePrice] = useState<number>(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [taxType, setTaxType] = useState(
        item.taxable_item ? "exclusive" : "inclusive"
    );
    const [alternateTax, setAlternateTax] = useState<AlternateTax[]>([]);
    const [variablePrice, setVariablePrice] = useState(item.is_variable_price);
    const [token, setToken] = useState(item.token_price !== null);
    const [thumbBase64, setThumbBase64] = useState<string>(item.image ?? "");
    const [modifiersValid, setModifiersValid] = useState(true);

    const handleThumbReady = async (blob: Blob) => {
        const dataUrl = await blobToDataURL(blob);
        console.log("Data URL" + dataUrl)
        const rawBase64 = dataUrlToRawBase64(dataUrl);
        console.log("Base64" + rawBase64)
        setThumbBase64(rawBase64);
    };

    async function blobToDataURL(blob: Blob): Promise<string> {
        return await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result));
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        })
    }

    function dataUrlToRawBase64(dataUrl: string): string {
        const [, raw = ""] = dataUrl.split(",", 2);
        return raw;
    }

    function handleClearImage() {
        setThumbBase64("");
    }

    const handleAddLineTax = () => {
        setAlternateTax([...alternateTax, {description: "", taxPercentage: 0}]);
        setIsAddClicked(true);
    };

    const handleTaxTypeChange = (value: string) => {
        setTaxType(value);
    };
    const handleRemoveLastTax = () => {
        setAlternateTax(alternateTax.slice(0, -1));
        if (alternateTax.length === 1) {
            setIsAddClicked(false);
        }
    };
    const handlePriceChange = (value: number) => {
        setPrice(value);
    };
    const handleTaxPercentageChange = (value: string | number | undefined) => {
        if (typeof value === "string") {
            setTaxPercentage(parseFloat(value) || 0);
        }
    };
    const handleLineItemChange = (
        index: number,
        field: string,
        value: string | number | undefined
    ) => {
        const newLineItems = [...alternateTax];
        newLineItems[index] = {...newLineItems[index], [field]: value ?? ""};
        setAlternateTax(newLineItems);
    };

    const calculateTotals = () => {
        let totalLineItemTax = 0;

        alternateTax.forEach((item) => {
            const itemTax = (price * (item.taxPercentage || 0)) / 100;
            totalLineItemTax += itemTax;
        });
        const mainTaxPercentage = taxPercentage || 0;

        // Handle case when price is 0 (Custom Price enabled)
        if (price === 0 || variablePrice) {
            setInclusivePrice(0);
            setTotalTax(0);
            setGrandTotal(0);
            return;
        }

        if (taxType === "inclusive") {
            const mainTaxRate = mainTaxPercentage / 100;
            const lineItemTaxRate = totalLineItemTax / price;
            const combinedTaxRate = mainTaxRate + lineItemTaxRate;
            // RP-1955: the typed price is what the customer pays. The base is the floor in whole cents
            // and the remainder is tax, the rule the tablets use for inclusive modifiers.
            const menuCents = Math.round(price * 100);
            const baseCents = inclusiveBaseCents(menuCents, combinedTaxRate);
            setInclusivePrice(baseCents / 100);
            setTotalTax((menuCents - baseCents) / 100);
            setGrandTotal(price);
        } else {
            const mainTax = (price * mainTaxPercentage) / 100;
            const totalTax = mainTax + totalLineItemTax;
            setTotalTax(totalTax);
            setGrandTotal(price + totalTax);
        }
    };

    const organizationId = getOrganizationStore().organizationId;

    useEffect(() => {
        calculateTotals();
    }, [alternateTax, taxType, price, taxPercentage, calculateTotals]);

    useEffect(() => {
        if (Array.isArray(item.alternate_tax) && item.alternate_tax.length > 0) {
            setAlternateTax(
                item.alternate_tax.map(
                    (tax: { description: string; taxPercentage: string }) => ({
                        description: tax.description || "",
                        taxPercentage: parseFloat(tax.taxPercentage) || 0,
                    })
                )
            );
            setIsAddClicked(true);
        }
    }, [item]);
    const clonedItemName = `${item.name} - clone`;
    return (
        <Form
            method="post"
            action={`/items/${item.id}/clone`}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
            onSubmit={(e) => {
                if (!modifiersValid) {
                    e.preventDefault();
                    toast.error("Please complete all modifier selections before saving");
                }
            }}
        >
            <FormLayout>
                <div className="col-span-5 sm:col-span-3">
                    <Input
                        label="Item Name"
                        name="name"
                        defaultValue={clonedItemName}
                        required
                    />
                </div>
                <IndeterminateCheckbox
                    label="Is Active?"
                    name="is_active"
                    defaultChecked={item.is_active}
                />
                <div className="col-span-6">
                    <CustomSelect
                        title="Event"
                        name="event_id"
                        defaultValue={item.event_id}
                        required
                        options={[
                            ...(events?.map((e) => ({
                                label: e.name,
                                value: e.id,
                            })) || []),
                        ]}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Vendor"
                        name="vendor_id"
                        defaultValue={item.vendor_id}
                        required
                        options={[
                            {
                                value: 0,
                                label: "All Vendors",
                            },
                            ...(vendors?.map((v) => ({
                                label: v.name,
                                value: v.id,
                            })) || []),
                        ]}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Item Category"
                        name="category"
                        defaultValue={item.category}
                        required
                        options={[
                            ...(itemCategories?.map((v) => ({
                                label: v.reference_label,
                                value: v.reference_value,
                            })) || []),
                        ]}
                    />
                </div>
                <div className="col-span-6">
                    <TextArea
                        label="Description"
                        name="description"
                        defaultValue={item.description}
                    />
                </div>
                <div className="col-span-6">
                    <Input label="UPC" name="upc" defaultValue={item.upc}/>
                </div>
                <div className="col-span-6">
                    <TagsInput
                        label="Optional Tags"
                        name="tags"
                        defaultValue={item.tags}
                        placeholder="Add a Tag (press 'enter' to add)"
                    />
                </div>
                <div className="col-span-6 sm:col-span-3">
                    <Input
                        label="GL Account"
                        name="gl_account_name"
                        defaultValue={item.gl_account_name}
                    />
                </div>
                <div className="col-span-6 sm:col-span-3">
                    <Input
                        label="GL Account Number"
                        name="gl_account_number"
                        defaultValue={item.gl_account_number}
                    />
                </div>
                {!variablePrice && (
                    <div className="col-span-5 sm:col-span-3">
                        <PriceInput
                            label="Menu Price"
                            name="price"
                            required
                            onChange={(value) => handlePriceChange(value ? value : 0)}
                            value={price}
                        />
                    </div>
                )}
                {variablePrice && <input type="hidden" value={"0"} name="price"/>}
                <div
                    className={
                        variablePrice
                            ? "col-span-6 sm:col-span-3"
                            : "col-span-1 sm:col-span-1"
                    }
                >
                    <IndeterminateCheckbox
                        label="Custom Price?"
                        name="is_variable_price"
                        defaultChecked={variablePrice}
                        onChange={(checked) => setVariablePrice(checked as boolean)}
                    />
                </div>
                <br/>
                <div className="col-span-3 sm:col-span-3">
                    <CustomSelect
                        title="Tax Type"
                        name="taxable_item"
                        options={[
                            {label: "Inclusive", value: "inclusive"},
                            {label: "Exclusive", value: "exclusive"},
                        ]}
                        defaultValue={taxed}
                        onChange={handleTaxTypeChange}
                    />
                </div>
                <div className="col-span-5 sm:col-span-3">
                    <Input
                        label="Tax % ( 7.5 = 7.5% )"
                        name="tax_percentage"
                        required
                        defaultValue={taxPercentage}
                        onChange={handleTaxPercentageChange}
                    />
                </div>
                {taxed && (
                    <>
                        {isAddClicked &&
                            alternateTax.map((tax, index) => (
                                <div key={index} className="col-span-6">
                                    <div className="col-span-6" key={index}>
                                        <Input
                                            label="Tax name"
                                            name={`alternate_tax_description_${index}`}
                                            value={tax.description}
                                            onChange={(value) =>
                                                handleLineItemChange(index, "description", value)
                                            }
                                        />
                                    </div>
                                    <div className="col-span-6">
                                        <Input
                                            label="Tax % ( 7.5 = 7.5% )"
                                            name={`alternate_tax_percentage_${index}`}
                                            value={tax.taxPercentage}
                                            required
                                            onChange={(value) =>
                                                handleLineItemChange(index, "taxPercentage", value)
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        {/*<div className="col-span-6 flex gap-4">*/}
                        {/*    <PrimaryButton type="button" onClick={handleAddLineTax}>*/}
                        {/*        + Alternate Tax*/}
                        {/*    </PrimaryButton>*/}
                        {/*    {isAddClicked && alternateTax.length > 0 && (*/}
                        {/*        <PrimaryButton*/}
                        {/*            type="button"*/}
                        {/*            onClick={handleRemoveLastTax}*/}
                        {/*        >*/}
                        {/*            - Alternate Tax*/}
                        {/*        </PrimaryButton>*/}
                        {/*    )}*/}
                        {/*</div>*/}
                    </>
                )}
                <div className="col-span-6 sm:col-span-1">
                    <h3 className="text-lg font-semibold">Price Preview</h3>
                    <div>
                        Price: $
                        {taxType === "inclusive"
                            ? inclusivePrice.toFixed(2)
                            : price.toFixed(2)}
                    </div>
                    <div>Total Tax: ${totalTax.toFixed(2)}</div>
                    <div>Grand Total: ${grandTotal.toFixed(2)}</div>
                </div>
                {thumbBase64 && (
                    <div className="col-span-3 sm:col-span-1 sm:col-start-3">
                        <div className="relative w-[125px] h-[125px]">
                            <img
                                src={`data:image/webp;base64,${thumbBase64}`}
                                width={125}
                                height={125}
                                alt="thumb"
                                className="rounded border border-slate-200 object-cover"
                            />
                            <button
                                type="button"
                                onClick={handleClearImage}
                                aria-label="Remove image"
                                title="Remove image"
                                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white
                            flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f04345]"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
                <div className="col-span-3 sm:col-span-1 sm:col-start-4">
                    <h3 className="text-lg font-semibold m-0">Upload Image
                        <CompactThumbUploader onReady={handleThumbReady}/></h3>
                </div>
                <div className="col-span-6 sm:col-span-2">
                    <CustomSelect
                        title="Image from Library"
                        name="image_library"
                        value={thumbBase64}
                        onChange={(v) => {
                            setThumbBase64(v);
                        }}
                        options={imageOptions}
                    />
                </div>
                <div className="col-span-6">
                    <>
                        <IndeterminateCheckbox
                            label="Token Eligible"
                            defaultChecked={token}
                            onChange={(checked) => setToken(checked as boolean)}
                        />
                        {token && (
                            <Input
                                label="Token Redemption Ratio (example: 1, 2, 3)"
                                name="token_price"
                                defaultValue={item.token_price}
                                required
                            />
                        )}
                        {token && (
                            <Input
                                label="Token Name (example: VIP TOKEN)"
                                name="redeemable_token_name"
                                defaultValue={item.redeemable_token_name}
                                required
                            />
                        )}
                    </>
                </div>
                <ModifierSelector
                    organizationId={organizationId}
                    initialModifierType={item.modifier_type}
                    initialModifiers={item.modifiers}
                    onValidationChange={setModifiersValid}
                />
                <input
                    type="hidden"
                    value={JSON.stringify(alternateTax)}
                    name="alternate_tax"
                />
                <input
                    type="hidden"
                    value={inclusivePrice.toFixed(2)}
                    name="inclusive_price"
                />
                <input
                    type="hidden"
                    value={thumbBase64 ?? ""}
                    name="image"/>
            </FormLayout>
        </Form>
    );
};
